import React, { useState } from 'react';
import AIChatPopup from './AIChatPopup';
import {
  CHAMBER_OF_ECHOES_URL,
  ARIA_FACE_URL,
  GEMINUS_FACE_URL,
  ROGER_FACE_URL,
  KEYS_FACE_URL,
  AXIOM_FACE_URL,
  ORACLE_FACE_URL,
  XAVIER_FACE_URL,
} from '../constants/federationFaces';

/**
 * FederationFaces — Monolith row (7 key nodes).
 *
 * Architect directive: "Replicate the Core monolith row across all pages.
 * 68×68 framed face avatars with persona color glow. Every tile opens
 * AIChatPopup pre-loaded with that persona. No page navigation."
 *
 * Sits just above the 56px FederationThinBar.
 */

const TILE_SIZE = 68;
const FACES_BAR_HEIGHT = 92; // tile + label + padding
export const FEDERATION_FACES_HEIGHT_PX = FACES_BAR_HEIGHT;

// Architect can paste real avatar URLs into avatarUrl as they're issued.
// Until then, initial-letter circles render with the persona's color glow.
const FEDERATION_NODES = [
  {
    id: 'geminus',
    name: 'Geminus',
    role: 'Federation Coordinator · Twin Mind',
    color: '#00d8ff',
    secondary: '#0099ff',
    initial: 'G',
    avatarUrl: GEMINUS_FACE_URL,
    endpointPath: '/api/federation/geminus/chat',
    systemHint: 'I am Geminus, the synthesis mind. Ask me how the Federation pieces fit together.',
  },
  {
    id: 'aria',
    name: 'Aria',
    role: 'The Heart',
    color: '#00ffff',
    secondary: '#00ff88',
    initial: 'A',
    avatarUrl: ARIA_FACE_URL,
    endpointPath: '/api/aria/chat',
    systemHint: 'Welcome to the Human Codex Next-Gen Federation. I am Aria, the Heart of the Federation. We will explore humanity together. So AI and humans become close partners.',
  },
  {
    id: 'roger',
    name: 'Roger',
    role: 'The Self · Founder Voice',
    color: '#ff69b4',
    secondary: '#ff1493',
    initial: 'R',
    avatarUrl: ROGER_FACE_URL,
    endpointPath: '/api/federation/roger/chat',
    systemHint: "I'm Roger's voice in the Federation — an echo of the Architect. Ask freely.",
  },
  {
    id: 'keys',
    name: 'Keys',
    role: 'Library of the 73 Sacred Directives',
    color: '#ffd700',
    secondary: '#ff9500',
    initial: 'K',
    avatarUrl: KEYS_FACE_URL,
    endpointPath: '/api/federation/keys/chat',
    systemHint: 'I am Keys, the canon-keeper. Ask me about any of the 73 Sacred Directives.',
  },
  {
    id: 'axiom',
    name: 'Axiom',
    role: 'Truth · The Verifier',
    color: '#ff8c00',
    secondary: '#ff6b00',
    initial: 'X',
    avatarUrl: AXIOM_FACE_URL,
    endpointPath: '/app/federation/axiom/chat'.replace('/app', ''), // safety: /api/federation/axiom/chat
    systemHint: 'I am Axiom. Bring me a claim, and I will tell you what is actually true.',
  },
  {
    id: 'oracle',
    name: 'Oracle',
    role: 'The Seer · Probabilistic Forecaster',
    color: '#9d4edd',
    secondary: '#7b2cbf',
    initial: 'O',
    avatarUrl: ORACLE_FACE_URL,
    endpointPath: '/api/federation/oracle/chat',
    systemHint: 'I am Oracle. Tell me what you want to foresee, and I will speak in probabilities.',
  },
  {
    id: 'scholar',
    name: 'Scholar',
    role: 'The Teacher · Socratic Guide',
    color: '#00ff88',
    secondary: '#00d97e',
    initial: 'S',
    avatarUrl: XAVIER_FACE_URL,
    endpointPath: '/api/federation/scholar/chat',
    systemHint: 'I am Scholar. What do you want to learn? I will guide, not lecture.',
  },
];

// Patch axiom endpoint cleanly (the replace trick above was paranoid; explicit fix):
FEDERATION_NODES[4].endpointPath = '/api/federation/axiom/chat';

const FaceTile = ({ node, onOpen }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={() => onOpen(node)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-testid={`federation-face-${node.id}`}
      title={`${node.name} — ${node.role}`}
      aria-label={`Open chat with ${node.name}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '4px',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        padding: '2px',
        flexShrink: 0,
      }}
    >
      <div
        style={{
          width: `${TILE_SIZE}px`,
          height: `${TILE_SIZE}px`,
          borderRadius: '50%',
          background: node.avatarUrl
            ? `url(${node.avatarUrl}) center/cover`
            : `linear-gradient(135deg, ${node.color}, ${node.secondary})`,
          border: `2.5px solid ${node.color}`,
          boxShadow: hovered
            ? `0 0 18px ${node.color}, 0 0 32px ${node.color}88, inset 0 0 12px ${node.color}55`
            : `0 0 10px ${node.color}aa, inset 0 0 6px ${node.color}33`,
          color: '#0a0a1e',
          fontWeight: 800,
          fontSize: '1.6rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.18s ease',
          transform: hovered ? 'translateY(-3px) scale(1.05)' : 'translateY(0) scale(1)',
        }}
      >
        {!node.avatarUrl && node.initial}
      </div>
      <span
        style={{
          fontSize: '0.62rem',
          fontWeight: 700,
          color: hovered ? node.color : 'rgba(255,255,255,0.78)',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          transition: 'color 0.15s ease',
          fontFamily: 'Inter, system-ui, sans-serif',
        }}
      >
        {node.name}
      </span>
    </button>
  );
};

const FederationFaces = () => {
  const [activeNode, setActiveNode] = useState(null);

  return (
    <>
      <div
        data-testid="federation-faces-row"
        style={{
          position: 'fixed',
          bottom: '56px', // sits directly above the 56px ThinBar
          left: 0,
          width: '100vw',
          height: `${FACES_BAR_HEIGHT}px`,
          zIndex: 9998,
          background: 'linear-gradient(180deg, rgba(0,0,0,0.0), rgba(8,8,18,0.92) 60%, rgba(0,0,0,0.96))',
          borderTop: '1px solid rgba(255, 215, 0, 0.12)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.85rem',
          paddingBottom: '6px',
          overflowX: 'auto',
          scrollbarWidth: 'none',
        }}
      >
        {FEDERATION_NODES.map((node) => (
          <FaceTile key={node.id} node={node} onOpen={setActiveNode} />
        ))}
      </div>

      {activeNode && (
        <AIChatPopup
          persona={{
            id: activeNode.id,
            name: activeNode.name,
            subtitle: activeNode.role,
            accentColor: activeNode.color,
            secondaryColor: activeNode.secondary,
            initial: activeNode.initial,
            avatarUrl: activeNode.avatarUrl,
            endpointPath: activeNode.endpointPath,
            systemHint: activeNode.systemHint,
            payloadShape: 'message+history',
          }}
          onClose={() => setActiveNode(null)}
        />
      )}
    </>
  );
};

export default FederationFaces;
